import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, X, Minus, Plus, PackageCheck, PackageX, ScanBarcode, Flashlight } from "lucide-react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { Button } from "../components/ui/button";
import { PassiveBadge } from "../components/ui/data-presentation";

export default function MobileInventory() {
  const { warehouses } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeWarehouse, setActiveWarehouse] = useState(null);
  const [searchModel, setSearchModel] = useState("");
  const [searchPart, setSearchPart] = useState("");
  const [showZero, setShowZero] = useState(false);

  const [scanOpen, setScanOpen] = useState(false);

  const handleScan = useCallback((text) => {
    setSearchPart(text);
    setScanOpen(false);
  }, []);

  // Hold bottom sheet state
  const [holdItem, setHoldItem] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [holdQty, setHoldQty] = useState(1);
  const [holdNotes, setHoldNotes] = useState("");
  const [holdLoading, setHoldLoading] = useState(false);

  // Set default warehouse on load
  useEffect(() => {
    if (warehouses.length && !activeWarehouse) {
      setActiveWarehouse(warehouses[0]);
    }
  }, [warehouses, activeWarehouse]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/items");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (holdItem) {
      document.body.style.overflow = "hidden";
      const t = setTimeout(() => setSheetOpen(true), 10);
      return () => clearTimeout(t);
    } else {
      setSheetOpen(false);
      document.body.style.overflow = "";
    }
  }, [holdItem]);

  // SKU count per warehouse (stock > 0)
  const warehouseCounts = warehouses.reduce((acc, w) => {
    acc[w.id] = items.filter((item) => (item.stock?.[w.id] ?? 0) > 0).length;
    return acc;
  }, {});

  const formatPrice = (price) => {
    if (!price) return "—";
    return "Rp " + price.toLocaleString("id-ID");
  };

  // Filtered items: stock filter + model/part search
  const filteredItems = items.filter((item) => {
    if (!activeWarehouse) return false;
    const stock = item.stock?.[activeWarehouse.id] ?? 0;
    if (!showZero && stock <= 0) return false;

    if (searchModel) {
      const q = searchModel.toLowerCase();
      const matchesModel = (item.model || "").toLowerCase().includes(q);
      const matchesCompat = (item.compatible_models || []).some((m) =>
        m.toLowerCase().includes(q)
      );
      if (!matchesModel && !matchesCompat) return false;
    }

    if (searchPart) {
      const q = searchPart.toLowerCase();
      const matchesPart = (item.part_name || "").toLowerCase().includes(q);
      const matchesNum = (item.part_number || "").toLowerCase().includes(q);
      if (!matchesPart && !matchesNum) return false;
    }

    return true;
  });

  const openHold = (item) => {
    setHoldQty(1);
    setHoldNotes("");
    setHoldItem(item);
  };

  const closeHold = () => {
    setSheetOpen(false);
    setTimeout(() => setHoldItem(null), 300);
  };

  const submitHold = async () => {
    if (!holdItem || !activeWarehouse) return;
    const available = holdItem.stock?.[activeWarehouse.id] ?? 0;
    if (holdQty < 1) return toast.error("Minimum quantity is 1");
    if (holdQty > available) return toast.error("Quantity exceeds available stock");

    setHoldLoading(true);
    try {
      await api.post("/holds", {
        item_id: holdItem.id,
        warehouse_id: activeWarehouse.id,
        quantity: holdQty,
        notes: holdNotes,
      });
      toast.success(`Hold placed: ${holdItem.part_number}`);
      closeHold();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHoldLoading(false);
    }
  };

  const activeStock = holdItem
    ? (holdItem.stock?.[activeWarehouse?.id] ?? 0)
    : 0;

  return (
    <div className="bg-background text-foreground flex flex-col" data-testid="mobile-inventory">

      {/* Warehouse tabs — index divider style */}
      <div
        className="bg-card px-4 pt-3 border-b border-border"
      >
        <div
          className="flex items-end gap-2 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
          role="tablist"
          aria-label="Warehouse"
        >
          {warehouses.map((w) => {
            const isActive = activeWarehouse?.id === w.id;
            const count = warehouseCounts[w.id] ?? 0;
            return (
              <button
                key={w.id}
                onClick={() => setActiveWarehouse(w)}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-accent"
                }`}
                style={{
                  borderRadius: "8px 8px 0 0",
                  ...(isActive
                    ? { marginBottom: -1, position: "relative", zIndex: 1 }
                    : {}),
                }}
              >
                {({ MAIN: "MAIN", SBY: "SRBY", DPS: "BALI" })[w.code] ?? w.code}
                <span
                  className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                    isActive
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-background/70 text-muted-foreground"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search inputs */}
      <div className="px-4 py-3 space-y-2 bg-card border-b border-border">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
            <input
              type="text"
              value={searchModel}
              onChange={(e) => setSearchModel(e.target.value)}
              placeholder="Search model..."
              className="w-full pl-9 pr-9 py-2.5 text-sm border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
            {searchModel && (
              <Button
                type="button"
                onClick={() => setSearchModel("")}
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400"
                aria-label="Clear model search"
                data-action-category="utility"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          <Button
            type="button"
            onClick={() => setShowZero((v) => !v)}
            variant="neutral"
            size="icon"
            className="h-10 w-12 shrink-0 self-center"
            title={showZero ? "Showing zero-stock items" : "Hiding zero-stock items"}
            aria-label={showZero ? "Hide zero-stock items" : "Show zero-stock items"}
            aria-pressed={showZero}
            data-action-category="utility"
          >
            {showZero ? (
              <PackageX className="h-4 w-4" aria-hidden="true" />
            ) : (
              <PackageCheck className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
            <input
              type="text"
              value={searchPart}
              onChange={(e) => setSearchPart(e.target.value)}
              placeholder="Search part..."
              className="w-full pl-9 pr-9 py-2.5 text-sm border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
            {searchPart && (
              <Button
                type="button"
                onClick={() => setSearchPart("")}
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400"
                aria-label="Clear part search"
                data-action-category="utility"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          <Button
            type="button"
            onClick={() => setScanOpen(true)}
            variant="info"
            size="icon"
            className="h-10 w-12 shrink-0 self-center"
            title="Scan barcode"
            aria-label="Scan barcode"
            data-action-category="view-info"
          >
            <ScanBarcode className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Item cards */}
      <div className="flex-1 px-4 py-3 space-y-2 overflow-y-auto pb-6">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16 text-zinc-400 text-sm">
            No items found
          </div>
        ) : (
          filteredItems.map((item) => {
            const stock = item.stock?.[activeWarehouse?.id] ?? 0;
            const threshold = item.low_stock_threshold ?? 0;
            const isLow = stock <= threshold;
            const compatModels = (item.compatible_models || []).filter(
              (m) => m !== item.model
            );

            return (
              <div
                key={item.id}
                className="bg-card rounded-xl border border-border p-4"
              >
                {/* Top: model name + price */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-foreground leading-snug">
                      {item.model}
                    </div>
                    {compatModels.length > 0 && (
                      <div className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed">
                        {compatModels.join(", ")}
                      </div>
                    )}
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-muted-foreground whitespace-nowrap">
                    {formatPrice(item.price)}
                  </span>
                </div>

                {/* Bottom: part info + stock badge + hold button */}
                <div className="flex items-end justify-between gap-2 mt-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {item.part_name}
                    </div>
                    <div className="text-[11px] font-semibold tracking-[0.04em] text-zinc-400 mt-0.5">
                      {item.part_number}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <PassiveBadge tone={stock === 0 ? "danger" : isLow ? "warning" : "success"}>
                      {stock}
                    </PassiveBadge>
                    <Button
                      type="button"
                      onClick={() => openHold(item)}
                      disabled={stock === 0}
                      className="h-11 min-w-[60px] px-3 text-xs disabled:shadow-none"
                      data-action-category="create-add"
                    >
                      Hold
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Scan Overlay */}
      {scanOpen && (
        <ScanOverlay onClose={() => setScanOpen(false)} onScan={handleScan} />
      )}

      {/* Hold Bottom Sheet */}
      {holdItem && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-20 transition-opacity duration-300"
            style={{ opacity: sheetOpen ? 1 : 0 }}
            onClick={closeHold}
          />

          {/* Sheet */}
          <div
            className="fixed bottom-0 left-0 right-0 bg-card text-card-foreground rounded-t-2xl z-30 shadow-2xl transition-transform duration-300 ease-out"
            style={{ transform: sheetOpen ? "translateY(0)" : "translateY(100%)" }}
          >
            <div className="px-5 pt-4 pb-8">
              {/* Handle */}
              <div className="w-10 h-1 bg-zinc-300 rounded-full mx-auto mb-5" />

              {/* Summary */}
              <div className="bg-muted/50 rounded-xl p-4 mb-5 space-y-2.5 text-sm">
                <SummaryRow label="Warehouse" value={activeWarehouse?.name} />
                <SummaryRow
                  label="Model"
                  value={
                    <span>
                      {holdItem.model}
                      {(() => {
                        const compat = (holdItem.compatible_models || []).filter(
                          (m) => m !== holdItem.model
                        );
                        return compat.length > 0 ? (
                          <span className="block text-xs text-zinc-400 font-normal mt-0.5">
                            {compat.join(", ")}
                          </span>
                        ) : null;
                      })()}
                    </span>
                  }
                />
                <SummaryRow label="Part" value={holdItem.part_name} />
                <SummaryRow
                  label="Part No."
                  value={<span className="text-xs font-semibold tracking-[0.04em]">{holdItem.part_number}</span>}
                />
                <SummaryRow label="Available" value={activeStock} />
              </div>

              {/* Qty stepper */}
              <div className="mb-4">
                <div className="text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-2.5">
                  Quantity
                </div>
                <div className="flex items-center gap-5">
                  <Button
                    type="button"
                    onClick={() => setHoldQty((q) => Math.max(1, q - 1))}
                    variant="neutral"
                    size="icon"
                    className="h-11 w-11"
                    aria-label="Decrease hold quantity"
                    data-action-category="utility"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-2xl font-semibold min-w-[2ch] text-center">
                    {holdQty}
                  </span>
                  <Button
                    type="button"
                    onClick={() => setHoldQty((q) => Math.min(activeStock, q + 1))}
                    variant="neutral"
                    size="icon"
                    className="h-11 w-11"
                    aria-label="Increase hold quantity"
                    data-action-category="utility"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {holdQty > activeStock && (
                  <div className="text-xs text-red-500 mt-1.5">
                    Exceeds available stock ({activeStock})
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="mb-5">
                <div className="text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-2.5">
                  Notes (optional)
                </div>
                <input
                  type="text"
                  value={holdNotes}
                  onChange={(e) => setHoldNotes(e.target.value)}
                  placeholder="Reason for hold..."
                  className="w-full border border-border bg-background rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary"
                  style={{ minHeight: 48 }}
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={closeHold}
                  variant="cancel"
                  className="h-[52px] flex-1"
                  data-action-category="cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={submitHold}
                  disabled={holdQty > activeStock || holdQty < 1}
                  loading={holdLoading}
                  loadingLabel="Submitting..."
                  variant="success"
                  className="h-[52px] flex-1 disabled:shadow-none"
                  data-action-category="submit-confirm"
                >
                  Submit Hold
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <span className="text-zinc-500 shrink-0">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}

function ScanOverlay({ onClose, onScan }) {
  const [denied, setDenied] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const html5QrCodeRef = useRef(null);
  const streamRef = useRef(null);

  const releaseCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    const html5QrCode = new Html5Qrcode("qr-reader");

    html5QrCode
      .start(
        { facingMode: "environment" },
        { fps: 15, aspectRatio: 1.777 },
        async (decodedText) => {
          try {
            await html5QrCodeRef.current?.applyVideoConstraints({
              advanced: [{ torch: false }],
            });
          } catch {}
          const state = html5QrCode.getState();
          if (
            state === Html5QrcodeScannerState.SCANNING ||
            state === Html5QrcodeScannerState.PAUSED
          ) {
            html5QrCode.stop()
              .catch(() => {})
              .finally(() => {
                releaseCamera();
                onScan(decodedText);
              });
          } else {
            onScan(decodedText);
          }
        },
        () => {}
      )
      .then(() => {
        html5QrCodeRef.current = html5QrCode;
        const video = document.querySelector("#qr-reader video");
        if (video) {
          video.style.position = "fixed";
          video.style.inset = "0";
          video.style.width = "100vw";
          video.style.height = "100vh";
          video.style.objectFit = "cover";
          video.style.zIndex = "0";
          if (video.srcObject) streamRef.current = video.srcObject;
        }
      })
      .catch((err) => {
        const name = err?.name || "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          setDenied(true);
        }
      });

    return () => {
      releaseCamera();
      try {
        const state = html5QrCode.getState();
        if (
          state === Html5QrcodeScannerState.SCANNING ||
          state === Html5QrcodeScannerState.PAUSED
        ) {
          html5QrCode.stop().catch(() => {});
        }
        html5QrCode.clear().catch(() => {});
      } catch {}
    };
  }, [onScan]);

  const toggleTorch = async () => {
    if (!html5QrCodeRef.current) return;
    try {
      await html5QrCodeRef.current.applyVideoConstraints({
        advanced: [{ torch: !torchOn }],
      });
      setTorchOn((prev) => !prev);
    } catch {
      alert("Flash tidak didukung di perangkat ini");
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black">
      {/* Scanner mount point */}
      <div id="qr-reader" style={{
        position: "absolute", inset: 0,
        width: "100%", height: "100%", overflow: "visible"
      }} />

      {/* Custom guide frame - ONE frame only */}
      {!denied && (
        <div style={{
          position: "absolute",
          top: "50%", left: "50%",
          transform: "translate(-50%, -60%)",
          width: 280, height: 110,
          border: "2px solid white",
          borderRadius: 8,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
          zIndex: 5,
          pointerEvents: "none"
        }} />
      )}

      {/* Close button */}
      <Button
        type="button"
        onClick={onClose}
        variant="ghost"
        size="icon"
        className="absolute left-4 top-4 z-10 h-11 w-11 border-white/20 bg-black/55 text-white hover:bg-black/70 hover:text-white"
        aria-label="Close scanner"
        data-action-category="close"
      >
        <X style={{ width: 22, height: 22 }} />
      </Button>

      {/* Flash / torch button */}
      <Button
        type="button"
        onClick={toggleTorch}
        variant={torchOn ? "info" : "neutral"}
        size="icon"
        className="absolute bottom-10 right-4 z-10 h-11 w-11"
        aria-label={torchOn ? "Turn flashlight off" : "Turn flashlight on"}
        aria-pressed={torchOn}
        data-action-category="utility"
      >
        <Flashlight className="h-[22px] w-[22px]" />
      </Button>

      {denied ? (
        <p style={{
          position: "absolute", top: "50%", left: 0, right: 0,
          transform: "translateY(-50%)", color: "white",
          fontSize: 15, textAlign: "center",
          padding: "0 32px", zIndex: 10
        }}>
          Camera access denied. Please allow camera access in your browser settings.
        </p>
      ) : (
        <p style={{
          position: "absolute", bottom: "28%",
          left: 0, right: 0, textAlign: "center",
          color: "rgba(255,255,255,0.7)",
          fontSize: 13, zIndex: 10
        }}>
          Aim the barcode at the guide
        </p>
      )}
    </div>
  );
}
